import React, { useState, useEffect } from 'react'
import { 
  BiSearch, 
  BiFilterAlt, 
  BiDownload,
  BiCheckCircle,
  BiTime,
  BiX,
  BiChevronDown,
  BiPackage,
  BiDetail,
  BiUser,
  BiPrinter,
  BiCalendar,
  BiDollar,
  BiShoppingBag,
  BiRightArrowAlt,
  BiSort,
  BiEdit,
  BiArrowBack
} from 'react-icons/bi'
import './Orders.css'
import { FaChevronDown } from 'react-icons/fa'

function Orders({ theme, projects }) {
  const [allOrders, setAllOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [orders, setOrders] = useState([]);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [sortOption, setSortOption] = useState('date-desc');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [detailsView, setDetailsView] = useState(false);
  const [statusDropdownStates, setStatusDropdownStates] = useState({});
  const [isDetailStatusDropdownOpen, setIsDetailStatusDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [projectsWithOrders, setProjectsWithOrders] = useState([]);

  // Process projects and orders on mount
  useEffect(() => {
    if (!projects) return;

    // Create project options including "All Projects"
    const allProjectsOption = {
      siteId: 'all',
      name: 'All Projects'
    };

    // Include all projects in the dropdown list
    setProjectsWithOrders([allProjectsOption, ...projects]);
    setSelectedProject('all'); // Default to "All Projects"

    // Process all orders from projects
    const processedOrders = projects.flatMap(project => 
      (project.orders || []).map(order => ({
        ...order,
        id: order._id || Math.random().toString(36).substr(2, 9),
        projectId: project.siteId,
        projectName: project.name,
        date: new Date(order.createdAt).toLocaleDateString(),
        time: new Date(order.createdAt).toLocaleTimeString(),
        customer: {
          name: order.customer?.customerData?.fullName || 'N/A',
          email: order.customer?.customerData?.email || 'N/A',
          id: order.customer?._id || 'N/A'
        },
        shippingAddress: {
          street: order.customer?.customerData?.address || 'N/A',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },
        items: order.items || [],
        orderDetails: {
          subtotal: Number(order.orderDetails?.subtotal || 0),
          shipping: Number(order.orderDetails?.shipping || 0),
          tax: Number(order.orderDetails?.tax || 0),
          total: Number(order.orderDetails?.total || 0)
        },
        status: order.status || 'pending',
        paymentStatus: 'pending'
      }))
    ).sort((a, b) => new Date(b.date) - new Date(a.date));

    setAllOrders(processedOrders);
    setOrders(processedOrders);
    setIsLoading(false);
  }, [projects]);

  // Filter orders based on selected project
  useEffect(() => {
    if (selectedProject === 'all') {
      setOrders(allOrders);
    } else {
      setOrders(allOrders.filter(order => order.projectId === selectedProject));
    }
  }, [selectedProject, allOrders]);

  // Get the selected order object
  const selectedOrder = orders.find(order => order.id === selectedOrderId) || null;

  // Get the selected project name
  const getSelectedProjectName = () => {
    const project = projectsWithOrders.find(p => p.siteId === selectedProject);
    return project ? project.name : 'All Projects';
  };

  // Status options for the filter
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Get the selected status label
  const getSelectedStatusLabel = () => {
    const status = statusOptions.find(s => s.value === statusFilter);
    return status ? status.label : 'All Statuses';
  };

  // Sort options
  const sortOptions = [
    { value: 'date-desc', label: 'Date (Newest First)' },
    { value: 'date-asc', label: 'Date (Oldest First)' },
    { value: 'amount-desc', label: 'Amount (Highest First)' },
    { value: 'amount-asc', label: 'Amount (Lowest First)' },
    { value: 'order-id', label: 'Order ID' }
  ];

  // Get the selected sort option label
  const getSelectedSortLabel = () => {
    const option = sortOptions.find(o => o.value === sortOption);
    return option ? option.label : 'Date (Newest First)';
  };

  // Filter and sort orders
  const filteredOrders = orders
    .filter(order => {
      // Status filter
      if (statusFilter !== 'all' && order.status !== statusFilter) {
        return false;
      }

      // Search filter
      const searchLower = searchQuery.toLowerCase();
      return (
        order.id.toLowerCase().includes(searchLower) ||
        order.customer.name.toLowerCase().includes(searchLower) ||
        order.customer.email.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      switch (sortOption) {
        case 'date-asc':
          return new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time);
        case 'date-desc':
          return new Date(b.date + ' ' + b.time) - new Date(a.date + ' ' + a.time);
        case 'amount-asc':
          return a.total - b.total;
        case 'amount-desc':
          return b.total - a.total;
        case 'order-id':
          return a.id.localeCompare(b.id);
        default:
          return new Date(b.date + ' ' + b.time) - new Date(a.date + ' ' + a.time);
      }
    });

  // Toggle project dropdown
  const toggleProjectDropdown = () => {
    setIsProjectDropdownOpen(!isProjectDropdownOpen);
    setIsStatusDropdownOpen(false);
  };

  // Toggle status dropdown
  const toggleStatusDropdown = () => {
    setIsStatusDropdownOpen(!isStatusDropdownOpen);
    setIsProjectDropdownOpen(false);
  };

  // Select a project
  const selectProject = (projectId) => {
    setSelectedProject(projectId);
    setIsProjectDropdownOpen(false);
  };

  // Select a status filter
  const selectStatus = (status) => {
    setStatusFilter(status);
    setIsStatusDropdownOpen(false);
  };

  // Handle sort option change
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  // View order details
  const viewOrderDetails = (orderId) => {
    setSelectedOrderId(orderId);
    setDetailsView(true);
  };

  // Close order details view
  const closeOrderDetails = () => {
    setDetailsView(false);
    setSelectedOrderId(null);
  };

  // Format date in a more readable way
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get appropriate status styling class
  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'processing':
        return 'status-processing';
      case 'pending':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  // Get appropriate payment status styling class
  const getPaymentStatusClass = (status) => {
    switch (status) {
      case 'paid':
        return 'payment-paid';
      case 'unpaid':
        return 'payment-unpaid';
      case 'refunded':
        return 'payment-refunded';
      default:
        return '';
    }
  };

  // Get Icon for order status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <BiCheckCircle />;
      case 'processing':
        return <BiTime />;
      case 'pending':
        return <BiPackage />;
      case 'cancelled':
        return <BiX />;
      default:
        return null;
    }
  };

  // Calculate order metrics
  const getTotalOrders = () => orders.length;
  const getTotalRevenue = () => orders.reduce((sum, order) => sum + order.total, 0).toFixed(2);
  const getCompletedOrders = () => orders.filter(order => order.status === 'completed').length;
  const getPendingOrders = () => orders.filter(order => order.status === 'pending').length;

  // Change order status
  const changeOrderStatus = async (orderId, newStatus) => {
    try {
      // Update local state
      const updatedOrders = allOrders.map(order => 
        order.id === orderId
          ? { ...order, status: newStatus }
          : order
      );

      setAllOrders(updatedOrders);
      setOrders(updatedOrders.filter(order => 
        selectedProject === 'all' || order.projectId === selectedProject
      ));

      // Show notification
      setNotification({
        message: `Order ${orderId} status changed to ${newStatus}`,
        type: 'success'
      });

      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating order status:', error);
      setNotification({
        message: 'Failed to update order status',
        type: 'error'
      });
    }

    // Close status dropdowns
    setStatusDropdownStates({});
    setIsDetailStatusDropdownOpen(false);
  };

  // Function to toggle status dropdown for a specific order
  const toggleStatusDropdownForOrder = (e, orderId) => {
    e.stopPropagation(); // Prevent row click event
    setStatusDropdownStates(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  return (
    <div className="orders-container" data-theme={theme}>
      {isLoading ? (
        <div className="loading-spinner">Loading orders...</div>
      ) : (
        <div className="workspace-tabs">
          <div className="workspace-tab active">Orders Management</div>
          <div className="workspace-tab starter">Analytics</div>
          
          <div className="workspace-metrics">
            <div className="workspace-metric">
              <span className="metric-label">Total Orders</span>
              <span className="metric-value-small">{getTotalOrders()}</span>
            </div>
            <div className="workspace-metric">
              <span className="metric-label">Revenue</span>
              <span className="metric-value-small">${getTotalRevenue()}</span>
            </div>
          </div>
        </div>
      )}

      {!detailsView ? (
        // Orders list view
        <>
          <div className="order-metrics">
            <div className="metric-card">
              <div className="metric-icon completed">
                <BiCheckCircle />
              </div>
              <div className="metric-content">
                <div className="metric-label">Completed</div>
                <div className="metric-value">{getCompletedOrders()}</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon pending">
                <BiTime />
              </div>
              <div className="metric-content">
                <div className="metric-label">Pending</div>
                <div className="metric-value">{getPendingOrders()}</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon revenue">
                <BiDollar />
              </div>
              <div className="metric-content">
                <div className="metric-label">Revenue</div>
                <div className="metric-value">${getTotalRevenue()}</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon total">
                <BiShoppingBag />
              </div>
              <div className="metric-content">
                <div className="metric-label">Total Orders</div>
                <div className="metric-value">{getTotalOrders()}</div>
              </div>
            </div>
          </div>

          <div className="order-filters">
            <div className="filters-left">
              {/* Project Selector */}
              <div className="filter-group">
                <div className="filter-label">Project:</div>
                <div className="filter-dropdown-container">
                  <div 
                    className="filter-dropdown" 
                    onClick={toggleProjectDropdown}
                  >
                    <span>{getSelectedProjectName()}</span>
                    <FaChevronDown />
                  </div>
                  
                  {isProjectDropdownOpen && (
                    <div className="dropdown-menu">
                      {projectsWithOrders.map(project => {
                        // Count orders for this project
                        const orderCount = project.siteId === 'all' 
                          ? allOrders.length 
                          : allOrders.filter(order => order.projectId === project.siteId).length;
                        
                        return (
                          <div 
                            key={project.siteId} 
                            className={`dropdown-item ${selectedProject === project.siteId ? 'active' : ''}`}
                            onClick={() => selectProject(project.siteId)}
                          >
                            <span className="project-name">{project.name}</span>
                            <span className="submission-count">
                              {orderCount > 0 ? `(${orderCount})` : '(0)'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Status Selector */}
              <div className="filter-group">
                <div className="filter-label">Status:</div>
                <div className="filter-dropdown-container">
                  <div 
                    className="filter-dropdown" 
                    onClick={toggleStatusDropdown}
                  >
                    <span>{getSelectedStatusLabel()}</span>
                    <BiChevronDown />
                  </div>
                  
                  {isStatusDropdownOpen && (
                    <div className="dropdown-menu">
                      {statusOptions.map(status => (
                        <div 
                          key={status.value} 
                          className={`dropdown-item ${statusFilter === status.value ? 'active' : ''}`}
                          onClick={() => selectStatus(status.value)}
                        >
                          {status.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="filters-right">
              {/* Search */}
              <div className="order-search">
                <BiSearch className="search-icon" />
                <input 
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>

              {/* Sort */}
              <div className="sort-container">
                <BiSort className="sort-icon" />
                <select 
                  className="sort-select" 
                  value={sortOption}
                  onChange={handleSortChange}
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map(order => (
                    <tr key={order.id} onClick={() => viewOrderDetails(order.id)}>
                      <td className="order-id">{order.id}</td>
                      <td className="customer-info">
                        <div>{order.customer?.name || 'N/A'}</div>
                        <div className="customer-email">{order.customer?.email || 'N/A'}</div>
                      </td>
                      <td className="order-date">
                        <div>{order.date || 'N/A'}</div>
                        <div className="order-time">{order.time || 'N/A'}</div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="status-cell">
                          <div 
                            className={`order-status ${getStatusClass(order.status)}`}
                            onClick={(e) => toggleStatusDropdownForOrder(e, order.id)}
                          >
                            <span className="status-icon">{getStatusIcon(order.status)}</span>
                            <span className="status-text">{(order.status || 'pending').charAt(0).toUpperCase() + (order.status || 'pending').slice(1)}</span>
                            <BiChevronDown className="status-dropdown-icon" />
                          </div>
                          
                          {statusDropdownStates[order.id] && (
                            <div className="status-dropdown">
                              {['pending', 'processing', 'completed', 'cancelled'].map(status => (
                                <div 
                                  key={status}
                                  className={`status-option ${status === order.status ? 'active' : ''}`}
                                  onClick={() => changeOrderStatus(order.id, status)}
                                >
                                  <span className={`status-option-icon ${getStatusClass(status)}`}>
                                    {getStatusIcon(status)}
                                  </span>
                                  <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={`payment-status ${getPaymentStatusClass(order.paymentStatus || 'pending')}`}>
                          {(order.paymentStatus || 'pending').charAt(0).toUpperCase() + (order.paymentStatus || 'pending').slice(1)}
                        </div>
                      </td>
                      <td className="order-amount">${(Number(order.orderDetails?.total || 0)).toFixed(2)}</td>
                      <td>
                        <div className="order-actions">
                          <button className="action-btn view-btn" onClick={(e) => {
                            e.stopPropagation();
                            viewOrderDetails(order.id);
                          }}>
                            <BiDetail />
                          </button>
                          <button className="action-btn print-btn" onClick={(e) => e.stopPropagation()}>
                            <BiPrinter />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="no-orders">
                    <td colSpan="7">
                      <div className="no-data">
                        <div className="no-data-icon"><BiShoppingBag /></div>
                        <h3>No orders found</h3>
                        <p>No orders match your current filter criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredOrders.length > 0 && (
            <div className="table-footer">
              <div className="showing-info">
                Showing {filteredOrders.length} of {orders.length} orders
              </div>
              <div className="pagination">
                <button className="pagination-btn" disabled>Previous</button>
                <button className="pagination-btn active">1</button>
                <button className="pagination-btn" disabled>Next</button>
              </div>
            </div>
          )}
        </>
      ) : (
        detailsView && selectedOrder ? (
          // Order details view
          <div className="order-details-container">
            <div className="order-details-header">
              <button className="back-button" onClick={closeOrderDetails}>
                <BiArrowBack />
                <span>Back to orders</span>
              </button>
              
              <div className="order-details-actions">
                <button className="toolbar-btn">
                  <BiPrinter />
                  <span>Print</span>
                </button>
                <button className="toolbar-btn">
                  <BiDownload />
                  <span>Export</span>
                </button>
              </div>
            </div>
            
            <div className="order-details-content">
              <div className="order-details-summary">
                <div className="order-details-summary-left">
                  <h2>Order {selectedOrder.id}</h2>
                  <div className={`order-details-status ${getStatusClass(selectedOrder.status)}`}>
                    <span className="status-icon">{getStatusIcon(selectedOrder.status)}</span>
                    <span className="status-text">{selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}</span>
                  </div>
                  <div className="status-actions">
                    <button 
                      className="change-status-btn"
                      onClick={() => setIsDetailStatusDropdownOpen(!isDetailStatusDropdownOpen)}
                    >
                      <BiEdit /> Change Status
                    </button>
                    
                    {isDetailStatusDropdownOpen && (
                      <div className="detail-status-dropdown">
                        {['pending', 'processing', 'completed', 'cancelled'].map(status => (
                          <div 
                            key={status}
                            className={`status-option ${status === selectedOrder.status ? 'active' : ''}`}
                            onClick={() => {
                              changeOrderStatus(selectedOrder.id, status);
                              setIsDetailStatusDropdownOpen(false);
                            }}
                          >
                            <span className={`status-option-icon ${getStatusClass(status)}`}>
                              {getStatusIcon(status)}
                            </span>
                            <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="order-details-summary-right">
                  <div className="summary-info">
                    <div className="summary-label"><BiCalendar /> Date:</div>
                    <div className="summary-value">{selectedOrder.date}</div>
                  </div>
                  <div className="summary-info">
                    <div className="summary-label"><BiUser /> Customer:</div>
                    <div className="summary-value">{selectedOrder.customer.name}</div>
                  </div>
                  <div className="summary-info">
                    <div className="summary-label"><BiPackage /> Items:</div>
                    <div className="summary-value">{selectedOrder.items.length}</div>
                  </div>
                </div>
              </div>
              
              <div className="order-details-sections">
                <div className="details-section">
                  <div className="section-title">Customer Information</div>
                  <div className="section-content">
                    <div className="customer-detail">
                      <strong>Name:</strong> {selectedOrder.customer.name}
                    </div>
                    <div className="customer-detail">
                      <strong>Email:</strong> {selectedOrder.customer.email}
                    </div>
                    <div className="customer-detail">
                      <strong>Customer ID:</strong> {selectedOrder.customer.id}
                    </div>
                  </div>
                </div>
                
                <div className="details-section">
                  <div className="section-title">Shipping Address</div>
                  <div className="section-content">
                    <div className="address-line">{selectedOrder.shippingAddress.street}</div>
                    {selectedOrder.shippingAddress.city && (
                      <div className="address-line">
                        {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.zipCode}
                      </div>
                    )}
                    {selectedOrder.shippingAddress.country && (
                      <div className="address-line">{selectedOrder.shippingAddress.country}</div>
                    )}
                  </div>
                </div>
                
                <div className="details-section full-width">
                  <div className="section-title">Order Items</div>
                  <div className="section-content">
                    <table className="order-items-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Price</th>
                          <th>Quantity</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.name}</td>
                            <td>${Number(item.price || 0).toFixed(2)}</td>
                            <td>{item.quantity}</td>
                            <td>${(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="details-section">
                  <div className="section-title">Order Summary</div>
                  <div className="section-content order-totals">
                    <div className="total-row">
                      <div className="total-label">Subtotal:</div>
                      <div className="total-value">
                        ${(Number(selectedOrder.orderDetails?.subtotal || 0)).toFixed(2)}
                      </div>
                    </div>
                    <div className="total-row">
                      <div className="total-label">Shipping:</div>
                      <div className="total-value">
                        ${(Number(selectedOrder.orderDetails?.shipping || 0)).toFixed(2)}
                      </div>
                    </div>
                    <div className="total-row">
                      <div className="total-label">Tax:</div>
                      <div className="total-value">
                        ${(Number(selectedOrder.orderDetails?.tax || 0)).toFixed(2)}
                      </div>
                    </div>
                    <div className="total-row grand-total">
                      <div className="total-label">Total:</div>
                      <div className="total-value">
                        ${(Number(selectedOrder.orderDetails?.total || 0)).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null
      )}

      {notification && (
        <div className="status-notification">
          <BiCheckCircle className="notification-icon" />
          <div className="notification-message">{notification.message}</div>
        </div>
      )}
    </div>
  )
}

export default Orders 